import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  getBroadcasts, createBroadcast, cancelBroadcast, retryBroadcast, sendBroadcastNow,
  getBroadcastRecipients, addBroadcastRecipient, excludeBroadcastRecipient,
  restoreBroadcastRecipient, syncBroadcastRecipients, setBroadcastRecipientDepartment,
} from "../api/guru";
import AccountMenu from "../components/AccountMenu";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import guruLogo from "../assets/guru-logo.png";

// ── Constants ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: "compose",    label: "✉️ Compose"    },
  { id: "history",    label: "📋 History"    },
  { id: "recipients", label: "👥 Recipients" },
];

const STATUS_BADGE = {
  scheduled: { label: "Scheduled", cls: "nbadge-scheduled" },
  sending:   { label: "Sending…",  cls: "nbadge-sending"   },
  sent:      { label: "Sent",      cls: "nbadge-sent"      },
  failed:    { label: "Failed",    cls: "nbadge-failed"    },
  cancelled: { label: "Cancelled", cls: "nbadge-cancelled" },
};

const fmt = {
  datetime: (iso) => iso ? new Date(iso).toLocaleString() : "—",
  date:     (iso) => iso ? new Date(iso).toLocaleDateString() : "—",
};

// ── Alert banner ──────────────────────────────────────────────────────────────

function Alert({ type, msg, onClose }) {
  if (!msg) return null;
  return (
    <div className={"alert alert-" + type} style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "inherit" }}>✕</button>
    </div>
  );
}

// ── Compose Panel ─────────────────────────────────────────────────────────────

function ComposePanel({ showAlert, onSent }) {
  const [subject,     setSubject]     = useState("");
  const [bodyText,    setBodyText]    = useState("");
  const [scheduleOn,  setScheduleOn]  = useState(false);
  const [scheduleAt,  setScheduleAt]  = useState("");
  const [sending,     setSending]     = useState(false);
  const [recipients,  setRecipients]  = useState([]);
  const [selected,    setSelected]    = useState(new Set());
  const [recipSearch, setRecipSearch] = useState("");
  const [loadingR,    setLoadingR]    = useState(true);

  const loadRecipients = useCallback(() => {
    setLoadingR(true);
    getBroadcastRecipients(false)
      .then(r => {
        const list = r.data.recipients || [];
        setRecipients(list);
        setSelected(new Set(list.map(rec => rec.email)));
      })
      .catch(() => showAlert("error", "Could not load recipient list."))
      .finally(() => setLoadingR(false));
  }, [showAlert]);

  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const filtered = recipients.filter(r =>
    r.email.toLowerCase().includes(recipSearch.toLowerCase()) ||
    (r.name || "").toLowerCase().includes(recipSearch.toLowerCase())
  );

  const toggleAll = () => {
    setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map(r => r.email)));
  };

  const toggleOne = (email) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !bodyText.trim()) {
      showAlert("error", "Subject and message body are required.");
      return;
    }
    if (selected.size === 0) {
      showAlert("error", "Select at least one recipient.");
      return;
    }
    setSending(true);
    try {
      const scheduledAt = scheduleOn && scheduleAt ? new Date(scheduleAt).toISOString() : null;
      const recipientList = Array.from(selected);
      await createBroadcast(subject, bodyText, "", scheduledAt, recipientList);
      showAlert("success", scheduledAt
        ? "Notification scheduled for " + fmt.datetime(scheduledAt) + "."
        : "Notification sent successfully!");
      setSubject(""); setBodyText(""); setScheduleOn(false); setScheduleAt("");
      onSent();
    } catch {
      showAlert("error", "Failed to send. Check SMTP config.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>Compose Notification</h2>
        <p className="panel-sub">Send an email to selected @88gravity.com employees.</p>
      </div>
      <form onSubmit={handleSend}>
        <div className="notifications-compose-grid">

          {/* ── Left: subject + body + schedule + send button ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Subject *</label>
              <input
                type="text"
                className="admin-inline-input"
                placeholder="e.g. Team Update — July 2026"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                disabled={sending}
                style={{ width: "100%" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Message *</label>
              <textarea
                className="admin-inline-input"
                placeholder="Write your message here…"
                value={bodyText}
                onChange={e => setBodyText(e.target.value)}
                disabled={sending}
                rows={12}
                style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }}
              />
            </div>

            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                <input type="checkbox" checked={scheduleOn} onChange={e => setScheduleOn(e.target.checked)} disabled={sending} />
                Schedule for later
              </label>
              {scheduleOn && (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="datetime-local"
                    className="admin-inline-input"
                    value={scheduleAt}
                    onChange={e => setScheduleAt(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    disabled={sending}
                    style={{ width: "100%", maxWidth: 320 }}
                  />
                </div>
              )}
            </div>

            <div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sending || !subject.trim() || !bodyText.trim() || selected.size === 0}
                style={{ minWidth: 160, fontSize: 14, padding: "10px 24px" }}
              >
                {sending ? "⏳ Sending…" : scheduleOn && scheduleAt ? "🕐 Schedule" : "📤 Send Now"}
              </button>
            </div>
          </div>

          {/* ── Right: recipient picker ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
              <label style={{ fontWeight: 600, fontSize: 13 }}>
                Recipients
                <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6 }}>
                  ({selected.size} of {recipients.length})
                </span>
              </label>
              <button type="button" className="btn btn-sm" onClick={toggleAll} style={{ fontSize: 12 }}>
                {selected.size === filtered.length ? "Deselect all" : "Select all"}
              </button>
            </div>
            <input
              type="text"
              placeholder="Search employees…"
              value={recipSearch}
              onChange={e => setRecipSearch(e.target.value)}
              style={{ padding: "7px 12px", border: "1px solid #e2e5ef", borderRadius: 7, fontSize: 13, width: "100%" }}
            />
            {loadingR ? (
              <div className="panel-loading">Loading…</div>
            ) : recipients.length === 0 ? (
              <div style={{ padding: 14, background: "#fef9c3", borderRadius: 8, fontSize: 13, color: "#92400e" }}>
                No recipients. Go to <strong>Recipients</strong> tab → <strong>Sync from Google</strong>.
              </div>
            ) : (
              <div className="n-recip-list" style={{ border: "1px solid #e2e5ef", borderRadius: 8, overflowY: "auto", maxHeight: 460 }}>
                {filtered.map(r => (
                  <label key={r.email} className={"n-recip-row" + (selected.has(r.email) ? " n-recip-row--selected" : "")} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                    cursor: "pointer", borderBottom: "1px solid #f3f4f6", fontSize: 13,
                  }}>
                    <input type="checkbox" checked={selected.has(r.email)} onChange={() => toggleOne(r.email)} disabled={sending} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 500, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.name || r.email}
                      </span>
                      {r.name && <span style={{ color: "#9ca3af", fontSize: 11 }}>{r.email}</span>}
                    </span>
                    {r.sync_source === "manual" && <span style={{ fontSize: 10, color: "#9ca3af", flexShrink: 0 }}>manual</span>}
                  </label>
                ))}
                {filtered.length === 0 && (
                  <div style={{ padding: 16, color: "#9ca3af", fontSize: 13 }}>No match for "{recipSearch}"</div>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

// ── History Panel ─────────────────────────────────────────────────────────────

function HistoryPanel({ showAlert }) {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getBroadcasts("", 100)
      .then(r => setBroadcasts(r.data.broadcasts || []))
      .catch(() => showAlert("error", "Could not load notification history."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this scheduled notification?")) return;
    try { await cancelBroadcast(id); showAlert("success", "Notification cancelled."); load(); }
    catch { showAlert("error", "Could not cancel."); }
  };

  const handleRetry = async (id) => {
    try { await retryBroadcast(id); showAlert("success", "Rescheduled — will send shortly."); load(); }
    catch { showAlert("error", "Could not retry."); }
  };

  const handleSendNow = async (id) => {
    if (!window.confirm("Send this notification immediately?")) return;
    try { await sendBroadcastNow(id); showAlert("success", "Sending now…"); setTimeout(load, 3000); }
    catch { showAlert("error", "Could not send."); }
  };

  if (loading) return <div className="admin-panel"><div className="panel-loading">Loading…</div></div>;

  return (
    <div className="admin-panel">
      <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2>Notification History</h2>
          <p className="panel-sub">{broadcasts.length} notification{broadcasts.length !== 1 ? "s" : ""} total</p>
        </div>
        <button className="btn btn-sm" onClick={load} style={{ fontSize: 12 }}>🔄 Refresh</button>
      </div>

      {broadcasts.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 14, padding: "20px 0" }}>No notifications sent yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {broadcasts.map(b => {
            const badge = STATUS_BADGE[b.status] || { label: b.status, cls: "nbadge-scheduled" };
            const isOpen = expanded === b.id;
            return (
              <div key={b.id} className="n-history-card" style={{ border: "1px solid #e2e5ef", borderRadius: 10, overflow: "hidden" }}>
                <div className="n-history-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer" }}
                  onClick={() => setExpanded(isOpen ? null : b.id)}>
                  <span className={"nbadge " + badge.cls}>{badge.label}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {b.subject}
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                      {b.status === "sent"
                        ? "Sent " + fmt.datetime(b.sent_at) + " · " + b.recipient_count + " recipient" + (b.recipient_count !== 1 ? "s" : "")
                        : b.status === "scheduled"
                          ? "Scheduled for " + (b.scheduled_at ? fmt.datetime(b.scheduled_at) : "immediate")
                          : b.status === "failed"
                            ? "Failed · created " + fmt.datetime(b.created_at)
                            : "Created " + fmt.datetime(b.created_at)}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    {b.status === "scheduled" && (
                      <>
                        <button className="btn btn-sm btn-primary" style={{ fontSize: 11, padding: "3px 10px" }}
                          onClick={e => { e.stopPropagation(); handleSendNow(b.id); }}>Send now</button>
                        <button className="btn btn-sm btn-danger" style={{ fontSize: 11, padding: "3px 10px" }}
                          onClick={e => { e.stopPropagation(); handleCancel(b.id); }}>Cancel</button>
                      </>
                    )}
                    {b.status === "failed" && (
                      <button className="btn btn-sm btn-primary" style={{ fontSize: 11, padding: "3px 10px" }}
                        onClick={e => { e.stopPropagation(); handleRetry(b.id); }}>Retry</button>
                    )}
                  </div>
                  <span style={{ color: "#9ca3af", fontSize: 14, marginLeft: 4 }}>{isOpen ? "▲" : "▼"}</span>
                </div>
                {isOpen && (
                  <div className="n-history-detail" style={{ borderTop: "1px solid #f3f4f6", padding: "12px 16px" }}>
                    <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, color: "#374151", lineHeight: 1.6, marginBottom: 10 }}>
                      {b.body_text}
                    </pre>
                    <div style={{ fontSize: 12, color: "#6b7280", display: "flex", gap: 20, flexWrap: "wrap" }}>
                      <span>Created by: <strong>{b.created_by || "—"}</strong></span>
                      <span>Created: <strong>{fmt.datetime(b.created_at)}</strong></span>
                      {b.status === "sent" && <span>Recipients: <strong>{b.recipient_count}</strong></span>}
                      {b.error_detail && <span style={{ color: "#dc2626" }}>Error: {b.error_detail}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Recipients Panel ──────────────────────────────────────────────────────────

const DEPARTMENTS = [
  "SEO", "Social Media", "Performance Marketing", "Content",
  "Development", "Design", "Sales", "HR", "Accounts", "Operations", "Management",
];

const DEPT_COLORS = {
  "SEO":                    { bg: "#eff6ff", color: "#1d4ed8" },
  "Social Media":           { bg: "#fdf4ff", color: "#7e22ce" },
  "Performance Marketing":  { bg: "#fff7ed", color: "#c2410c" },
  "Content":                { bg: "#f0fdf4", color: "#15803d" },
  "Development":            { bg: "#f0f9ff", color: "#0369a1" },
  "Design":                 { bg: "#fdf2f8", color: "#9d174d" },
  "Sales":                  { bg: "#fefce8", color: "#92400e" },
  "HR":                     { bg: "#f9fafb", color: "#374151" },
  "Accounts":               { bg: "#f0fdf4", color: "#166534" },
  "Operations":             { bg: "#fff1f2", color: "#be123c" },
  "Management":             { bg: "#fefce8", color: "#713f12" },
};

function DeptBadge({ dept }) {
  if (!dept) return <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>;
  const c = DEPT_COLORS[dept] || { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: c.bg, color: c.color }}>
      {dept}
    </span>
  );
}

function RecipientsPanel({ showAlert }) {
  const [recipients,   setRecipients]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [syncing,      setSyncing]      = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [search,       setSearch]       = useState("");
  const [newEmail,     setNewEmail]     = useState("");
  const [newName,      setNewName]      = useState("");
  const [adding,       setAdding]       = useState(false);
  const [deptEditing,  setDeptEditing]  = useState(null); // email being edited

  const load = useCallback(() => {
    setLoading(true);
    getBroadcastRecipients(showExcluded)
      .then(r => setRecipients(r.data.recipients || []))
      .catch(() => showAlert("error", "Could not load recipients."))
      .finally(() => setLoading(false));
  }, [showAlert, showExcluded]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await syncBroadcastRecipients();
      showAlert("success", "Synced " + r.data.synced_count + " accounts from Google directory.");
      load();
    } catch { showAlert("error", "Sync failed. Check DIRECTORY_OAUTH_REFRESH_TOKEN in .env."); }
    finally   { setSyncing(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await addBroadcastRecipient(newEmail.trim(), newName.trim());
      showAlert("success", newEmail.trim() + " added.");
      setNewEmail(""); setNewName(""); load();
    } catch { showAlert("error", "Could not add recipient."); }
    finally   { setAdding(false); }
  };

  const handleExclude = async (email) => {
    if (!window.confirm("Remove " + email + " from all future broadcasts?")) return;
    try { await excludeBroadcastRecipient(email); showAlert("success", email + " excluded."); load(); }
    catch { showAlert("error", "Could not exclude."); }
  };

  const handleRestore = async (email) => {
    try { await restoreBroadcastRecipient(email); showAlert("success", email + " restored."); load(); }
    catch { showAlert("error", "Could not restore."); }
  };

  const handleSetDept = async (email, dept) => {
    setDeptEditing(email);
    try {
      await setBroadcastRecipientDepartment(email, dept || null);
      showAlert("success", `Department updated for ${email}.`);
      load();
    } catch { showAlert("error", "Could not update department."); }
    finally { setDeptEditing(null); }
  };

  const filtered = recipients.filter(r =>
    r.email.toLowerCase().includes(search.toLowerCase()) ||
    (r.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const activeCount   = recipients.filter(r => !r.excluded).length;
  const excludedCount = recipients.filter(r => r.excluded).length;

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h2>Broadcast Recipients</h2>
            <p className="panel-sub">
              {activeCount} active{showExcluded ? " · " + excludedCount + " excluded" : ""}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={() => setShowExcluded(v => !v)} style={{ fontSize: 12 }}>
              {showExcluded ? "Hide excluded" : "Show excluded"}
            </button>
            <button className="btn btn-sm btn-primary" onClick={handleSync} disabled={syncing} style={{ fontSize: 12 }}>
              {syncing ? "⏳ Syncing…" : "🔄 Sync from Google"}
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <input type="email" className="admin-inline-input" placeholder="Email address"
          value={newEmail} onChange={e => setNewEmail(e.target.value)} disabled={adding} style={{ width: 220 }} />
        <input type="text" className="admin-inline-input" placeholder="Name (optional)"
          value={newName} onChange={e => setNewName(e.target.value)} disabled={adding} style={{ width: 180 }} />
        <button className="btn btn-primary btn-sm" disabled={adding || !newEmail.trim()}>
          {adding ? "Adding…" : "+ Add manually"}
        </button>
      </form>

      <div style={{ marginBottom: 12 }}>
        <input type="text" placeholder="Search recipients…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: "7px 12px", border: "1px solid #e2e5ef", borderRadius: 7, fontSize: 13, width: 360 }} />
      </div>

      {loading ? (
        <div className="panel-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 14, padding: "12px 0" }}>
          {recipients.length === 0
            ? "No recipients yet. Click \"Sync from Google\" to pull the employee list."
            : "No match."}
        </div>
      ) : (
        <div className="table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e2e5ef" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Email</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Department</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Source</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Last synced</th>
                <th style={{ padding: "8px 12px" }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.email} style={{ borderBottom: "1px solid #f3f4f6", opacity: r.excluded ? 0.5 : 1, background: r.excluded ? "#fef2f2" : "transparent" }}>
                  <td style={{ padding: "9px 12px" }}>
                    {r.email}
                    {r.excluded && <span style={{ marginLeft: 6, fontSize: 11, color: "#dc2626", fontWeight: 600 }}>EXCLUDED</span>}
                  </td>
                  <td style={{ padding: "9px 12px", color: "#374151" }}>{r.name || "—"}</td>
                  <td style={{ padding: "9px 12px" }}>
                    {deptEditing === r.email ? (
                      <span style={{ color: "#9ca3af", fontSize: 12 }}>Saving…</span>
                    ) : (
                      <select
                        value={r.department || ""}
                        onChange={e => handleSetDept(r.email, e.target.value)}
                        style={{ fontSize: 12, padding: "3px 6px", border: "1px solid #e2e5ef", borderRadius: 6, background: "#fff", cursor: "pointer" }}
                      >
                        <option value="">— Unassigned —</option>
                        {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    )}
                  </td>
                  <td style={{ padding: "9px 12px" }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                      background: r.sync_source === "manual" ? "#eff6ff" : "#f0fdf4",
                      color: r.sync_source === "manual" ? "#1d4ed8" : "#15803d",
                    }}>{r.sync_source === "manual" ? "Manual" : "Google"}</span>
                  </td>
                  <td style={{ padding: "9px 12px", color: "#9ca3af", fontSize: 12 }}>{r.last_synced_at ? fmt.date(r.last_synced_at) : "—"}</td>
                  <td style={{ padding: "9px 12px", textAlign: "right" }}>
                    {r.excluded
                      ? <button className="btn btn-sm btn-primary" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => handleRestore(r.email)}>Restore</button>
                      : <button className="btn btn-sm btn-danger"  style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => handleExclude(r.email)}>Remove</button>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Notifications() {
  const { user }                      = useAuth();
  const { darkTheme, setDarkTheme }   = useChat();
  const [tab, setTab]                 = useState("compose");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertType, setAlertType]     = useState("");
  const [alertMsg,  setAlertMsg]      = useState("");

  const showAlert = useCallback((type, msg) => {
    setAlertType(type);
    setAlertMsg(msg);
    setTimeout(() => setAlertMsg(""), 5000);
  }, []);

  const handleSent = () => setTab("history");

  return (
    <div className="chat-page">
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      {/* ── Sidebar ── */}
      <div className={"gpt-sidebar" + (sidebarOpen ? " gpt-sidebar-open" : "")}>
        <div className="gpt-sidebar-brand">
          <img src={guruLogo} alt="Guru" className="gpt-brand-logo" />
          <div>
            <div className="gpt-brand-name">Guru</div>
            <div className="gpt-brand-sub">SEO Knowledge Agent</div>
          </div>
        </div>
        <div className="gpt-sidebar-topnav">
          <NavLink to="/chat"          className="gpt-nav-link"><span>💬</span> Back to Chat</NavLink>
          <NavLink to="/admin"         className="gpt-nav-link"><span>⚙️</span> Admin</NavLink>
          <NavLink to="/projects"      className="gpt-nav-link"><span>📋</span> Projects</NavLink>
          <NavLink to="/notifications" className="gpt-nav-link active-nav"><span>📣</span> Notifications</NavLink>
          <NavLink to="/query-history" className="gpt-nav-link"><span>🔍</span> Query History</NavLink>
        </div>
        <div className="gpt-sidebar-spacer" />
        <div style={{ padding: "8px 16px", flexShrink: 0 }}>
          <button className="theme-toggle-btn" style={{ width: "100%", justifyContent: "flex-start", gap: "8px" }}
            onClick={() => setDarkTheme(d => !d)} title={darkTheme ? "Switch to light theme" : "Switch to dark theme"}>
            {darkTheme ? "☀️ Light mode" : "🌙 Dark mode"}
          </button>
        </div>
        <AccountMenu />
      </div>

      {/* ── Main content ── */}
      <div className="notifications-content">
        <div className="mobile-page-header">
          <button type="button" className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">☰</button>
          <h1>Notifications</h1>
        </div>
        <p className="subtitle">Broadcast emails to the 88gravity team</p>

        <Alert type={alertType} msg={alertMsg} onClose={() => setAlertMsg("")} />

        <div className="admin-tabs">
          {TABS.map(t => (
            <button key={t.id} className={"admin-tab" + (tab === t.id ? " active" : "")} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "compose"    && <ComposePanel    showAlert={showAlert} onSent={handleSent} />}
        {tab === "history"    && <HistoryPanel    showAlert={showAlert} />}
        {tab === "recipients" && <RecipientsPanel showAlert={showAlert} />}
      </div>
    </div>
  );
}
